import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Select, Space } from 'antd';
import { RestInputContext } from 'components/form/RestInputContext';
import FilterRow, { TFilterItem } from 'components/rest/Filter/FilterRow';
import { FilterContentWrapper } from 'components/rest/Filter/styles';
import { CONDITIONS } from 'constants/filters';
import dayjs, { Dayjs } from 'dayjs';
import { useGetRootPath } from 'hooks/useShowLayout';
import _, { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DynamicObjectType } from 'types/commons';

function removeDuplicates(arr) {
  const seen = new Set();
  const uniqueItems = [];

  arr.forEach((item) => {
    const identifier = `${item.condition}-${item.key}`;
    if (!seen.has(identifier)) {
      seen.add(identifier);
      uniqueItems.push(item);
    }
  });

  return uniqueItems;
}

export type TFilterRequest = {
  condition: 'and' | 'or';
  key: string;
  operator: 'is' | 'notis' | string;
  value:
    | null
    | string
    | number
    | string[]
    | number[]
    | boolean
    | Dayjs[]
    | DynamicObjectType[];
};
export type TFilterContent = {
  content?: React.ReactElement;
  filters?: TFilterItem[];
  filterData?: TFilterRequest[];
  setOpen?: (open: boolean) => void;
};

export default function FilterContent({
  content,
  filters,
  filterData,
  setOpen,
}: TFilterContent) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { rootPath, search } = useGetRootPath();
  const [record, setRecord] = useState({});

  const onFinish = (values) => {
    const keysWithDatePicker = _.chain(filters)
      .filter((item) => item?.isDatePicker === true)
      .map('key')
      .value();
    const keysWithArraySelect = _.chain(filters)
      .filter((item) => item?.isArraySelect === true)
      .map('key')
      .value();
    const keysWithCheckNull = _.chain(filters)
      .filter((item) => item?.isCheckNull === true)
      .map('key')
      .value();

    const keysWithSpecialCase = [
      ...keysWithDatePicker,
      ...keysWithArraySelect,
      ...keysWithCheckNull,
    ];
    const convertFilterData = [];

    if (values?.filters?.length) {
      values.filters.forEach((item) => {
        if (Array.isArray(item?.value) && item?.value?.length > 0) {
          const convertChild = [];

          if (item?.value?.length) {
            for (const val of item.value as any) {
              if (!val?.key) break;
              if (keysWithSpecialCase?.includes(val?.key)) {
                if (keysWithCheckNull?.includes(val?.key)) {
                  convertChild.push(
                    val?.value
                      ? {
                          ...val,
                          operator: 'notnull',
                          value: true,
                        }
                      : {
                          ...val,
                          operator: 'isnull',
                          value: true,
                        },
                  );
                }
                if (keysWithDatePicker?.includes(val?.key)) {
                  convertChild.push({
                    ...val,
                    value: [
                      // dayjs(val.value[0], 'YYYY-MM-DD 00:00:01'),
                      // dayjs(val?.value[1], 'YYYY-MM-DD 23:59:59').endOf('day'),
                      dayjs(val.value[0].format('YYYY-MM-DD'))
                        .add(-7, 'hours')
                        .add(1, 'second')
                        .format('YYYY-MM-DD HH:mm:ss'),
                      dayjs(val.value[1].format('YYYY-MM-DD'))
                        .add(1, 'day')
                        .add(-7, 'hours')
                        .subtract(1, 'second')
                        .format('YYYY-MM-DD HH:mm:ss'),
                    ],
                  });
                }
                if (keysWithArraySelect?.includes(val?.key)) {
                  convertChild.push({
                    ...val,
                    value: val.value?.map((data) => data?.id),
                  });
                }
              } else {
                convertChild.push(val);
              }
            }
          }

          console.log('convertChild123ss', convertChild);

          if (convertChild?.length) {
            convertFilterData.push({
              ...item,
              value: convertChild,
            });
          }
        }
      });
    }

    console.log('convertFilterData123', convertFilterData);

    const restFilters = [
      ...removeDuplicates([...convertFilterData]),
      ...(filterData?.filter(
        (item) =>
          !(Array.isArray(item.value) && typeof item?.value?.[0] === 'object'),
      ) || []),
    ];

    setOpen(false);
    const searchParams = new URLSearchParams(search);
    searchParams.delete('page');
    searchParams.delete('pageSize');
    searchParams.set('filters', JSON.stringify(restFilters, null, 0));
    navigate(`${rootPath}?${searchParams.toString()}`);
  };

  const onClear = () => {
    setOpen(false);
    const newFilters = filterData?.filter((i) => typeof i.key === 'string');
    const searchParams = new URLSearchParams(search);
    searchParams.delete('page');
    searchParams.delete('pageSize');
    if (newFilters?.length) {
      searchParams.set('filters', JSON.stringify(newFilters, null, 0));
    } else {
      searchParams.delete('filters');
    }

    const searchStr = searchParams.toString();

    navigate(`${rootPath}${searchStr ? `?${searchStr}` : ''}`);
  };

  useEffect(() => {
    const keysWithDatePicker = _.chain(filters)
      .filter((item) => item?.isDatePicker === true)
      .map('key')
      .value();
    const keysWithArraySelect = _.chain(filters)
      .filter((item) => item?.isArraySelect === true)
      .map('key')
      .value();
    const keysWithArrayCheckNull = _.chain(filters)
      .filter((item) => item?.isCheckNull === true)
      .map('key')
      .value();

    const keysWithSpecialCase = [
      ...keysWithDatePicker,
      ...keysWithArraySelect,
      ...keysWithArrayCheckNull,
    ];

    const convertFilterData = [];
    if (filterData?.length) {
      filterData.forEach((item) => {
        if (Array.isArray(item?.value) && item?.value?.length > 0) {
          const convertChild = [];
          if (item?.value?.length) {
            for (const val of item.value as any) {
              if (!val?.key) break;
              if (keysWithSpecialCase?.includes(val?.key)) {
                if (keysWithArrayCheckNull?.includes(val?.key)) {
                  convertChild.push({
                    ...val,
                    operator: 'is',
                    value: val?.operator === 'notnull',
                  });
                }
                if (keysWithDatePicker?.includes(val?.key)) {
                  convertChild.push({
                    ...val,
                    value: [
                      dayjs(val.value[0]).add(7, 'hours').startOf('day'),
                      dayjs(val.value[1]).add(7, 'hours').startOf('day'),
                    ],
                  });
                }
                if (keysWithArraySelect?.includes(val?.key)) {
                  convertChild.push({
                    ...val,
                    value: val.value?.map((data) => ({ id: data })),
                  });
                }
              } else {
                convertChild.push(val);
              }
            }
          }

          if (convertChild?.length) {
            convertFilterData.push({
              ...item,
              value: convertChild,
            });
          }
        }
      });
    }

    setRecord(
      convertFilterData?.length
        ? {
            filters: convertFilterData,
          }
        : {
            filters: [
              {
                condition: CONDITIONS[0].value,
                key: 0,
                // operator: OPERATORS[0].value,
                value: [
                  {
                    // operator: OPERATORS[0].value,
                    condition: CONDITIONS[0].value,
                  },
                ],
              },
            ],
          },
    );

    form.setFieldsValue(
      convertFilterData?.length
        ? {
            filters: convertFilterData,
          }
        : {
            filters: [
              {
                condition: CONDITIONS[0].value,
                key: 0,
                // operator: OPERATORS[0].value,
                value: [
                  {
                    // operator: OPERATORS[0].value,
                    condition: CONDITIONS[0].value,
                  },
                ],
              },
            ],
          },
    );
  }, [filterData, filters]);

  return (
    <RestInputContext.Provider
      value={{
        form,
        record: record,
      }}
    >
      <Form form={form} onFinish={onFinish}>
        {content || (
          <FilterContentWrapper>
            <h3 className="fw-600 text-sm mb-[12px]">{t('button.filter')}</h3>
            <Form.List name="filters">
              {(fields, { add, remove }) => {
                return (
                  <>
                    {fields.map((field) => (
                      <div className="flex items-center gap-[10px] mb-[15px]">
                        <div>
                          {fields.length <= 1 ? (
                            <></>
                          ) : (
                            <>
                              {field.name === 0 ? (
                                <Form.Item
                                  shouldUpdate={(o, n) => {
                                    return (
                                      get(o, ['filters'])?.[1]?.condition !==
                                      get(n, ['filters'])?.[1]?.condition
                                    );
                                  }}
                                >
                                  {({ getFieldValue, setFieldValue }) => {
                                    const currentFilters =
                                      getFieldValue('filters');
                                    setFieldValue(
                                      ['filters', 0, 'condition'],
                                      currentFilters?.[1]?.condition,
                                    );
                                    return (
                                      <Form.Item
                                        name={[field.name, 'condition']}
                                        className="w-[80px]"
                                      >
                                        Where
                                      </Form.Item>
                                    );
                                  }}
                                </Form.Item>
                              ) : (
                                <>
                                  {field.name === 1 ? (
                                    <Form.Item
                                      name={[field.name, 'condition']}
                                      className="w-[80px]"
                                    >
                                      <Select
                                        options={CONDITIONS}
                                        placeholder="Data"
                                      />
                                    </Form.Item>
                                  ) : (
                                    <Form.Item
                                      shouldUpdate={(o, n) => {
                                        return (
                                          get(o, ['filters'])?.[1]
                                            ?.condition !==
                                          get(n, ['filters'])?.[1]?.condition
                                        );
                                      }}
                                    >
                                      {({ getFieldValue, setFieldValue }) => {
                                        const currentFilters =
                                          getFieldValue('filters');
                                        setFieldValue(
                                          ['filters', field.name, 'condition'],
                                          currentFilters?.[1]?.condition,
                                        );

                                        return (
                                          <Form.Item
                                            name={[field.name, 'condition']}
                                            rules={[
                                              {
                                                required: true,
                                                message:
                                                  'Missing Root condition',
                                              },
                                            ]}
                                            className="w-[80px]"
                                          >
                                            <Input disabled />
                                          </Form.Item>
                                        );
                                      }}
                                    </Form.Item>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                        <div className="bg-[#F7F8F9] rounded-[5px] p-[10px] w-full">
                          <Form.Item>
                            <Form.List
                              name={[field.name, 'value']}
                              initialValue={[
                                {
                                  // operator: OPERATORS[0].value,
                                  condition: CONDITIONS[0].value,
                                },
                              ]}
                            >
                              {(subFields, subOpt) => (
                                <div className="min-w-[500px]">
                                  {subFields.map((subField) => (
                                    <FilterRow
                                      indexParent={field.name}
                                      remove={subOpt?.remove}
                                      filters={filters}
                                      hiddenRemove={
                                        subFields.length === 1 &&
                                        subField?.name === 0
                                      }
                                      subFields={subFields}
                                      subField={subField}
                                      form={form}
                                    />
                                  ))}
                                  <div className="flex items-center gap-[5px]">
                                    <Button
                                      type="text"
                                      onClick={() =>
                                        subOpt?.add({
                                          // operator: OPERATORS[0].value,
                                          condition: CONDITIONS[0].value,
                                        })
                                      }
                                      icon={
                                        <PlusOutlined className="text-[12px]" />
                                      }
                                      className="text-[12px]"
                                    >
                                      {t('button.addNestedFilter')}
                                    </Button>
                                    {fields.length > 1 && (
                                      <Button
                                        type="text"
                                        onClick={() => remove(field.name)}
                                        icon={
                                          <DeleteOutlined className="text-[12px]" />
                                        }
                                        className="text-[12px]"
                                      >
                                        {t('common.button.removeFilter')}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Form.List>
                          </Form.Item>
                        </div>
                      </div>
                    ))}
                    <div className="d-flex space-between">
                      {fields.length === filters.length ? (
                        <span />
                      ) : (
                        <Form.Item noStyle>
                          <Button
                            type="text"
                            onClick={() =>
                              add({
                                // operator: OPERATORS[0].value,
                                condition: CONDITIONS[0].value,
                                key: fields.length,
                              })
                            }
                            icon={<PlusOutlined />}
                          >
                            {t('button.addFilter')}
                          </Button>
                        </Form.Item>
                      )}

                      <Space align="end">
                        <Form.Item noStyle>
                          <Button
                            ghost
                            type="primary"
                            onClick={() => setOpen(false)}
                            block
                          >
                            {t('button.cancel')}
                          </Button>
                        </Form.Item>
                        <Button danger onClick={onClear} block>
                          {t('button.clear')}
                        </Button>
                        <Form.Item noStyle>
                          <Button
                            onClick={form.submit}
                            htmlType="submit"
                            type="primary"
                            block
                          >
                            {t('button.ok')}
                          </Button>
                        </Form.Item>
                      </Space>
                    </div>
                  </>
                );
              }}
            </Form.List>
          </FilterContentWrapper>
        )}
      </Form>
    </RestInputContext.Provider>
  );
}
